import { Clock3Icon } from "lucide-react";
import { Link } from "react-router";

import { EarningsCardAmounts } from "./EarningsCardAmounts";
import { EarningsCardRailSection } from "./EarningsCardRailSection";

import { Center } from "@/components/Center";
import { Column } from "@/components/Column";
import { ErrorText } from "@/components/ErrorText";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useGetStafferEarnings } from "@/hooks/queries/useGetStafferEarnings";
import { shortTimezoneLabel, timezoneToIana } from "@/lib/constants";
import { earningsProgress } from "@/lib/earningsProgress";
import { formatting } from "@/lib/formatting";
import { getCandidacyLink, getJobLink } from "@/lib/notificationLinks";
import { cn } from "@/lib/utils";
import { EarningsJob, EarningsJobInterview, Timezone } from "@/types";

const VISIBLE_CANDIDATE_AVATAR_COUNT = 2;

const AVATAR_COLOR_CLASSES = [
  "bg-blue-100 text-blue-700",
  "bg-green-100 text-green-700",
];

// "Jul 18, 5:00 AM", rendered in the interview's own timezone, with a suffix
// when it isn't Central. Null timezones fall back to the viewer's local time.
const formatInterviewDateTime = (interview: EarningsJobInterview): string => {
  const dateLabel = new Date(interview.dateTime).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(interview.timezone
      ? { timeZone: timezoneToIana[interview.timezone] }
      : {}),
  });

  if (interview.timezone && interview.timezone !== Timezone.CENTRAL) {
    return `${dateLabel} ${shortTimezoneLabel[interview.timezone]}`;
  }

  return dateLabel;
};

const getCandidateInitials = (name: string): string => {
  const nameParts = name.trim().split(/\s+/);

  return formatting.initials(nameParts[0], nameParts.at(-1));
};

const reqsLabel = (count: number): string => (count === 1 ? "req" : "reqs");

export const EarningsInterviewsSection = ({
  selectedUserId,
}: {
  selectedUserId?: number;
}) => {
  /* Database CRUD Operations */
  // Read
  const {
    data: earnings,
    isPending,
    isError,
  } = useGetStafferEarnings({ userId: selectedUserId });

  /* Helpers */
  // Each job's per-req commission is its revenue estimate stacked on top of
  // the current YTD basis, blended across any tier boundary it crosses.
  const getJobCommission = (job: EarningsJob): number => {
    if (!earnings) return 0;

    return earningsProgress.commissionBetween(
      earnings.totalRevenueYtd,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      earnings.totalRevenueYtd + (job.estimatedRevenueAmount || 0),
      earnings.commissionTiers,
    );
  };

  /* Render */
  const renderSkeleton = () => (
    <Column className="gap-3">
      <Skeleton className="h-3 w-44" />
      <Row className="gap-4">
        <Skeleton className="h-44 w-80 rounded-lg" />
        <Skeleton className="h-44 w-80 rounded-lg" />
      </Row>
    </Column>
  );

  const render = () => {
    if (isPending) {
      return renderSkeleton();
    }

    if (isError) {
      return <ErrorText errorMessage="Failed to load interviews." />;
    }

    const jobs = earnings.jobs
      .filter((job) => job.interviews.length > 0)
      .sort((a, b) =>
        a.interviews[0].dateTime.localeCompare(b.interviews[0].dateTime),
      );

    // No interviews hides the whole section
    if (jobs.length === 0) {
      return null;
    }

    const commissionTotal = jobs.reduce(
      (total, job) => total + getJobCommission(job),
      0,
    );

    return (
      <EarningsCardRailSection
        cardCount={jobs.length}
        commissionTotal={commissionTotal}
        label="Interviews"
      >
        {jobs.map((job) => (
          <InterviewJobCard
            key={job.jobId}
            commissionDollars={getJobCommission(job)}
            job={job}
          />
        ))}
      </EarningsCardRailSection>
    );
  };

  return render();
};

const InterviewJobCard = ({
  commissionDollars,
  job,
}: {
  commissionDollars: number;
  job: EarningsJob;
}) => {
  /* Helpers */
  // Interviews arrive sorted ascending; next = first upcoming, else most recent
  const now = new Date().toISOString();
  const nextInterview =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    job.interviews.find((interview) => interview.dateTime >= now) ||
    job.interviews[job.interviews.length - 1];
  const hasMultipleCandidates = job.interviews.length > 1;
  const hiddenCandidateCount =
    job.interviews.length - VISIBLE_CANDIDATE_AVATAR_COUNT;
  const candidateLabel = hasMultipleCandidates ? (
    `${job.interviews.length.toString()} candidates`
  ) : (
    <Link
      className="hover:underline"
      to={getCandidacyLink({
        candidacyId: job.interviews[0].candidacyId,
        companyId: job.companyId,
        jobId: job.jobId,
      })}
    >
      {job.interviews[0].candidateName}
    </Link>
  );

  /* Render */
  const renderReqCounts = () => {
    // Protect reqs stack under the earnable count so both read at a glance
    if (job.protectReqCount > 0) {
      return (
        <Column className="min-w-0 gap-0.5">
          <Text className="truncate text-xs font-medium text-slate-600">
            {candidateLabel} ·
          </Text>
          {job.earnableReqCount > 0 ? (
            <Text className="truncate text-xs font-medium text-slate-600">
              {job.earnableReqCount} earnable {reqsLabel(job.earnableReqCount)}
            </Text>
          ) : null}
          <Text className="truncate text-xs font-medium text-slate-600">
            {job.protectReqCount} {reqsLabel(job.protectReqCount)} to protect
          </Text>
        </Column>
      );
    }

    return (
      <Text className="min-w-0 truncate text-xs font-medium text-slate-600">
        {candidateLabel} · {job.earnableReqCount} open{" "}
        {reqsLabel(job.earnableReqCount)}
      </Text>
    );
  };

  const render = () => (
    <Card className="min-h-[220px] w-80 flex-none p-5 shadow-none">
      <Column className="h-full gap-4">
        <Column className="min-w-0 gap-0.5">
          <Link
            className="w-fit max-w-full"
            to={getJobLink({ companyId: job.companyId, jobId: job.jobId })}
          >
            <Text className="truncate text-sm font-medium text-slate-900 hover:underline">
              {job.jobTitle}
            </Text>
          </Link>
          <Text className="truncate text-xs font-normal text-slate-500">
            {job.companyName}
          </Text>
        </Column>
        <Row className="items-center gap-3">
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto rounded-full p-0 hover:bg-transparent"
                >
                  <Row className="items-center">
                    {job.interviews
                      .slice(0, VISIBLE_CANDIDATE_AVATAR_COUNT)
                      .map((interview, index) => (
                        <Center
                          key={interview.interviewId}
                          className={cn(
                            "h-7 w-7 rounded-full ring-2 ring-white",
                            index > 0 && "-ml-2",
                            AVATAR_COLOR_CLASSES[
                              index % AVATAR_COLOR_CLASSES.length
                            ],
                          )}
                        >
                          <Text className="text-[11px] font-semibold">
                            {getCandidateInitials(interview.candidateName)}
                          </Text>
                        </Center>
                      ))}
                    {hiddenCandidateCount > 0 ? (
                      <Center className="-ml-2 h-7 w-7 rounded-full bg-slate-100 ring-2 ring-white">
                        <Text className="text-[11px] font-semibold text-slate-600">
                          +{hiddenCandidateCount.toString()}
                        </Text>
                      </Center>
                    ) : null}
                  </Row>
                </Button>
              </TooltipTrigger>
              <TooltipContent align="start" side="top">
                <Column className="gap-2">
                  {job.interviews.map((interview) => (
                    <Row
                      key={interview.interviewId}
                      className="items-center justify-between gap-5"
                    >
                      <Link
                        to={getCandidacyLink({
                          candidacyId: interview.candidacyId,
                          companyId: job.companyId,
                          jobId: job.jobId,
                        })}
                      >
                        <Text className="text-xs font-medium text-slate-900 hover:underline">
                          {interview.candidateName}
                        </Text>
                      </Link>
                      <Text className="whitespace-nowrap text-xs font-normal text-slate-500">
                        {formatInterviewDateTime(interview)}
                      </Text>
                    </Row>
                  ))}
                </Column>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {renderReqCounts()}
        </Row>
        <Row className="min-w-0 items-center gap-2">
          <Clock3Icon className="h-4 w-4 shrink-0 text-slate-500" />
          <Text className="truncate text-xs font-normal text-slate-600">
            {hasMultipleCandidates ? (
              <>
                Next:{" "}
                <Link
                  className="hover:underline"
                  to={getCandidacyLink({
                    candidacyId: nextInterview.candidacyId,
                    companyId: job.companyId,
                    jobId: job.jobId,
                  })}
                >
                  {nextInterview.candidateName}
                </Link>
                {", "}
              </>
            ) : null}
            {formatInterviewDateTime(nextInterview)}
          </Text>
        </Row>
        <EarningsCardAmounts
          commissionDollars={commissionDollars}
          commissionLabel="Commission per req"
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          revenueDollars={job.estimatedRevenueAmount || 0}
        />
      </Column>
    </Card>
  );

  return render();
};
